import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Per-row MessageActions handlers. Read on demand via `getHandlers()`
 * so the LATEST closure fires when the user clicks — even if row
 * props (e.g. `isPinned`) change between mouseenter and click.
 */
export interface MessageActionHandlers {
  onEdit?: () => void;
  onDelete?: () => void;
  onReact?: (emoji: string) => void;
  onInlineReply?: () => void;
  onCreateThread?: () => void;
  onPin?: () => void;
  onSendToShell?: () => void;
}

export interface HoveredMessagePayload {
  messageId: string;
  /** The row's outer wrapper element — used for positioning the pill
   *  and for imperatively toggling its `actions-open` class. Held by
   *  reference; never null after a mouseenter dispatch. */
  element: HTMLElement;
  /** Returns the row's current action callbacks. The row owns a ref
   *  internally and updates it each render; this getter dereferences
   *  it at invoke time. Avoids exposing a `MutableRefObject` through
   *  the payload, which trips `react-hooks/refs` on render-time reads. */
  getHandlers: () => MessageActionHandlers;
  isOwn: boolean;
  isPinned: boolean;
}

/**
 * Hover context is split into TWO contexts to keep `<Message />` out
 * of the re-render path on hover changes:
 *
 *   • `HoveredMessageApiContext` — stable callbacks. Value identity
 *     never changes after the provider mounts. Consumed by every
 *     `<Message />` — they only *dispatch* into hover state from
 *     event handlers, never read `hovered` / `locked` during render.
 *
 *   • `HoveredMessageStateContext` — reactive `{ hovered, locked }`.
 *     Value changes on every hover. Consumed only by
 *     `<HoveredMessageActions />` (the singleton) which actually
 *     renders based on the state.
 *
 * Why split: a single combined context whose value changes on every
 * hover re-renders EVERY component that called `useContext` on it.
 * With ~200 Messages rendered in a scrolled-up window, each cursor
 * crossing between rows would re-run 200 Message renders + their
 * Zero selectors + handler closures — visible as cursor lag. After
 * the split, Messages subscribe only to the stable API → zero
 * re-renders on hover; the reactive state has a single subscriber.
 */
interface HoveredMessageApiValue {
  setHovered: (payload: HoveredMessagePayload) => void;
  /** Debounced clear (80 ms). Used for row mouseleave so the cursor
   *  can cross the small gap row→pill without losing hover. Skipped
   *  while `locked` is true. */
  clearHovered: () => void;
  /** Immediate clear with no debounce, no lock-respect. Used for scroll
   *  events: when the user scrolls, the pill should disappear right
   *  now (no following the row), and any locked emoji picker should
   *  also tear down — `MessageActions` listens to scroll separately to
   *  close the picker, but we don't want to race that path; clearing
   *  the lock here is idempotent. */
  clearImmediate: () => void;
  /** Cancel a pending debounced clear. Pill mouseenter calls this so
   *  the mouse-handoff row→pill doesn't lose the hover state. */
  cancelClear: () => void;
  setLocked: (locked: boolean) => void;
}

interface HoveredMessageStateValue {
  hovered: HoveredMessagePayload | null;
  locked: boolean;
}

const HoveredMessageApiContext = createContext<HoveredMessageApiValue | null>(
  null,
);
const HoveredMessageStateContext = createContext<HoveredMessageStateValue>({
  hovered: null,
  locked: false,
});

/** Mouseleave from row → row-clears debounced by this much, so a
 *  cursor that moves row→pill via the small gap between them doesn't
 *  flicker the pill. Cancelled by the pill's own mouseenter. */
const CLEAR_DEBOUNCE_MS = 80;

export function HoveredMessageProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [hovered, setHoveredState] = useState<HoveredMessagePayload | null>(
    null,
  );
  const [locked, setLockedState] = useState(false);

  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Locked is mirrored to a ref so the synchronous clear path can read
  // it without depending on React state batching. Without this, a
  // mouseleave that races with a setLocked(true) (e.g. user clicked
  // "more reactions" then moved off-row in the same frame) could clear
  // the locked picker.
  const lockedRef = useRef(false);

  // Track the currently-classed element so we can swap `actions-open`
  // imperatively, synchronously inside the mouse event handler. Doing
  // this in a useEffect/useLayoutEffect instead would race the
  // browser's own `:hover` flip: the browser updates `:hover` to row B
  // immediately on cursor cross, but React's render+commit cycle is
  // async. With effect-driven class swap, there's at least one paint
  // where row A still has `actions-open` (stale) AND row B has `:hover`
  // (just-flipped) — both rows visibly highlighted. Doing the swap
  // here in the same JS task as the mouse event guarantees only one
  // row is ever highlighted in any frame.
  const classedElementRef = useRef<HTMLElement | null>(null);

  const cancelClear = useCallback(() => {
    if (clearTimerRef.current !== null) {
      clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }
  }, []);

  const setHovered = useCallback(
    (payload: HoveredMessagePayload) => {
      cancelClear();
      // SYNC: swap actions-open class before the function returns. By
      // the time the browser repaints (end of this task), only the new
      // row has `actions-open`.
      if (
        classedElementRef.current &&
        classedElementRef.current !== payload.element
      ) {
        classedElementRef.current.classList.remove("actions-open");
      }
      payload.element.classList.add("actions-open");
      classedElementRef.current = payload.element;

      // SYNC: write pill position to CSS custom properties on the
      // document root. The singleton's portal div reads `top`/`right`
      // from these vars, so the pill repositions in the same paint as
      // the hover transition — no waiting on React render to land new
      // coords. Without this, the pill visibly lagged the cursor when
      // hovering down through messages.
      const rect = payload.element.getBoundingClientRect();
      const docEl = document.documentElement;
      docEl.style.setProperty(
        "--hovered-pill-top",
        `${Math.round(rect.top - 12)}px`,
      );
      docEl.style.setProperty(
        "--hovered-pill-right",
        `${Math.round(window.innerWidth - rect.right + 8)}px`,
      );

      setHoveredState((prev) => {
        // Identity-stable update when re-hovering the same row — avoids
        // a needless re-render of the singleton.
        if (
          prev &&
          prev.messageId === payload.messageId &&
          prev.element === payload.element &&
          prev.isOwn === payload.isOwn &&
          prev.isPinned === payload.isPinned &&
          prev.getHandlers === payload.getHandlers
        ) {
          return prev;
        }
        return payload;
      });
    },
    [cancelClear],
  );

  const removeClassedElement = useCallback(() => {
    if (classedElementRef.current) {
      classedElementRef.current.classList.remove("actions-open");
      classedElementRef.current = null;
    }
  }, []);

  const clearHovered = useCallback(() => {
    cancelClear();
    if (lockedRef.current) return;
    clearTimerRef.current = setTimeout(() => {
      clearTimerRef.current = null;
      if (lockedRef.current) return;
      removeClassedElement();
      setHoveredState(null);
    }, CLEAR_DEBOUNCE_MS);
  }, [cancelClear, removeClassedElement]);

  const clearImmediate = useCallback(() => {
    cancelClear();
    removeClassedElement();
    lockedRef.current = false;
    setLockedState(false);
    setHoveredState(null);
  }, [cancelClear, removeClassedElement]);

  const setLocked = useCallback((value: boolean) => {
    lockedRef.current = value;
    setLockedState(value);
  }, []);

  useEffect(() => {
    return () => {
      if (clearTimerRef.current !== null) clearTimeout(clearTimerRef.current);
    };
  }, []);

  // API value: stable for provider lifetime. All callbacks are
  // `useCallback`-stable, so this `useMemo` never recomputes (deps
  // never change), which means every `<Message />` subscribed to it
  // never re-renders due to hover changes.
  const apiValue = useMemo<HoveredMessageApiValue>(
    () => ({
      setHovered,
      clearHovered,
      clearImmediate,
      cancelClear,
      setLocked,
    }),
    [setHovered, clearHovered, clearImmediate, cancelClear, setLocked],
  );

  // State value: changes on every hover / lock flip. The singleton
  // `<HoveredMessageActions />` is the only subscriber.
  const stateValue = useMemo<HoveredMessageStateValue>(
    () => ({ hovered, locked }),
    [hovered, locked],
  );

  return (
    <HoveredMessageApiContext.Provider value={apiValue}>
      <HoveredMessageStateContext.Provider value={stateValue}>
        {children}
      </HoveredMessageStateContext.Provider>
    </HoveredMessageApiContext.Provider>
  );
}

/**
 * Stable API hook — dispatching into hover state. Returns null when no
 * provider is mounted (singleton flag off). Components that only need
 * to call `setHovered` / `clearHovered` / etc. from event handlers
 * should use this hook — it never causes re-renders on hover changes.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook + provider colocate by design; both are part of the same singleton contract
export function useHoveredMessageApi(): HoveredMessageApiValue | null {
  return useContext(HoveredMessageApiContext);
}

/**
 * Reactive state hook — `{ hovered, locked }`. Components that read
 * these values during render subscribe here. Triggers a re-render
 * every time hover or lock changes. Used by the singleton
 * `<HoveredMessageActions />`; per-row consumers should NOT use this.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook + provider colocate by design; both are part of the same singleton contract
export function useHoveredMessageState(): HoveredMessageStateValue {
  return useContext(HoveredMessageStateContext);
}
